import { useLogin, useRequestLink, useRequestPasswordReset } from '@myrentalmate/api-client';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { okData } from '../../lib/apiData';
import { brand } from '../../lib/brand';
import { TwoFactorChallenge } from './TwoFactorChallenge';

export function SignInPage({ orgName }: { orgName?: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const queryClient = useQueryClient();
  const [challenge, setChallenge] = useState<string | null>(null);
  const login = useLogin({
    mutation: {
      onSuccess: (response) => {
        const answer = okData(response);
        if (answer?.mfa_required) setChallenge(answer.challenge);
        else return queryClient.invalidateQueries();
      },
    },
  });

  const requestLink = useRequestLink();
  const requestReset = useRequestPasswordReset();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!password) return;
    login.mutate({ data: { email, password } });
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'center' }}>
          {orgName ?? brand.name}
        </Typography>
        {challenge ? (
          <TwoFactorChallenge challenge={challenge} onBack={() => setChallenge(null)} />
        ) : (
          <Stack component="form" spacing={2} onSubmit={submit}>
            {login.isError && <Alert severity="error">That email or password isn't right.</Alert>}
            <TextField
              label="Email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button type="submit" variant="contained" size="large" disabled={login.isPending}>
              {login.isPending ? 'Signing in…' : 'Sign in'}
            </Button>
            <Button
              variant="text"
              disabled={!email || requestLink.isPending}
              onClick={() => requestLink.mutate({ data: { email } })}
            >
              Email me a sign-in link instead
            </Button>
            <Button
              variant="text"
              disabled={!email || requestReset.isPending}
              onClick={() => requestReset.mutate({ data: { email } })}
            >
              Forgot your password?
            </Button>
            {requestReset.isSuccess && (
              <Alert severity="info">
                If that email has an account, a reset link is on its way. It expires in 30 minutes.
              </Alert>
            )}
            {requestLink.isSuccess && (
              <Alert severity="info">
                If that email has an account, a sign-in link is on its way. It expires in 15
                minutes.
              </Alert>
            )}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
